import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactHeader,
  ArtifactTitle,
} from "@/components/ai-elements/artifact";
import DataTableCompanyEmployees from "@/components/data-table-company-employees/data-table-company-employees";
import ErrorMessage from "@/components/error-message";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeams } from "@/contexts/teams-provider";
import { useApiQuery } from "@/hooks/useApiQuery";
import { usePeopleTableRowsWriter } from "@/hooks/usePeopleTableRowsWriter";
import { useTranslation } from "@/hooks/useTranslation";
import { liveRowToEmployee } from "@/lib/people-table";
import { searchKeys } from "@/lib/query-keys";
import {
  PeopleTableLive,
  chatArtifactPanelStore,
} from "@/stores/chat-artifact-panel-store";
import { SavedSearchDTO } from "@shaarp/common";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, Loader2Icon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
  SAVED_PEOPLE_TABLE_POLL_MS,
  getPeopleTablePanelState,
  isSavedPeopleTableSettled,
} from "./chat-people-table-panel-state";

type ChatPeopleTablePanelProps = {
  readonly searchId: string;
  /** Progress of the search filling the table, null once it is saved. */
  readonly live: PeopleTableLive | null;
  readonly className?: string;
  /** As `ChatArtifactPanel`'s: a back arrow when laid over the conversation. */
  readonly dismissAs?: "close" | "back";
};

/**
 * A saved search as the panel reads it, once `enabled`, and whether it holds
 * its run's final rows. Read again every few seconds until it does — after a
 * Stop, the server writes the rows a moment after the panel first asks.
 * Never refetched on focus: once settled, its rows only change by a click in
 * the table, which writes them into the cache itself
 * (`usePeopleTableRowsWriter`).
 */
const useSavedSearch = (
  teamId: string,
  searchId: string,
  enabled: boolean,
  watchedPeopleCount: number,
) => {
  const queryClient = useQueryClient();
  const key = searchKeys.detail(teamId, searchId);
  const query = useApiQuery<SavedSearchDTO>({
    key,
    path: `/search-engine/search/${searchId}`,
    enabled: enabled && !!teamId,
    refetchOnWindowFocus: false,
    refetchInterval: ({ state }) =>
      state.data &&
      !isSavedPeopleTableSettled({
        saved: state.data,
        readCount: state.dataUpdateCount,
        watchedPeopleCount,
      })
        ? SAVED_PEOPLE_TABLE_POLL_MS
        : false,
  });
  // Read off the cache, as the interval above reads it: the query's result
  // does not carry the count. `dataUpdatedAt` is what re-renders on each read,
  // even one that hands back the same search.
  const readCount = query.dataUpdatedAt
    ? (queryClient.getQueryState(key)?.dataUpdateCount ?? 0)
    : 0;
  const isSettled = isSavedPeopleTableSettled({
    saved: query.data,
    readCount,
    watchedPeopleCount,
  });
  return { saved: query.data, isSettled, isError: query.isError };
};

/**
 * A people table of the conversation, beside it: the table the prospecting
 * page shows for a people search — reveal, save to a list, export.
 *
 * While the search runs (`live`), the rows it streams fill that very table,
 * read-only; the saved rows take their place once it completes, so reveals
 * and lists land in the table the user has been reading.
 */
export function ChatPeopleTablePanel({
  searchId,
  live,
  className,
  dismissAs = "close",
}: ChatPeopleTablePanelProps) {
  const { t } = useTranslation();
  const { teamSelected } = useTeams();
  const teamId = teamSelected?.id ?? "";
  const isLive = live !== null;

  // The last progress the run reported, held past the moment it stops: the
  // saved rows are only read then, and the table would empty out for the
  // length of that request.
  const [lastLive, setLastLive] = useState<PeopleTableLive | null>(live);
  if (live !== null && live !== lastLive) {
    setLastLive(live);
  }

  // Only once the search is done: its rows are saved as it completes.
  const {
    saved,
    isSettled: isSaved,
    isError,
  } = useSavedSearch(teamId, searchId, !isLive, lastLive?.peopleCount ?? 0);

  const { isFilling, body, areRowActionsLocked, areRowChangesSaved } =
    getPeopleTablePanelState({
      hasWatchedRun: lastLive !== null,
      isSaved,
      isError,
    });

  // Stable identities: the table resets its rows whenever it gets new ones.
  const liveEmployees = useMemo(
    () => (lastLive?.rows ?? []).map(liveRowToEmployee),
    [lastLive],
  );
  // The watched rows until the saved ones are final: a saved search read
  // before its run wrote them holds none.
  const employees = useMemo(
    () => (isSaved && saved ? saved.employees : liveEmployees),
    [isSaved, saved, liveEmployees],
  );
  const handleRowsChange = usePeopleTableRowsWriter(teamId);

  const query = saved?.query ?? lastLive?.query ?? "";
  const totalCount = isFilling
    ? (lastLive?.peopleCount ?? 0)
    : employees.length;

  const dismiss = (): void => chatArtifactPanelStore.close();

  return (
    <Artifact className={className}>
      <ArtifactHeader>
        {dismissAs === "back" && (
          <ArtifactAction
            aria-label={t("chat.peopleTable.backToConversation")}
            onClick={dismiss}
            tooltip={t("chat.peopleTable.backToConversation")}
          >
            <ArrowLeftIcon className="size-4" />
          </ArtifactAction>
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <ArtifactTitle>{query || t("chat.peopleTable.untitled")}</ArtifactTitle>
          {isFilling && <PeopleTableFilling totalCount={totalCount} />}
          {!isFilling && isSaved && (
            <p className="text-xs truncate text-muted-foreground">
              {t("chat.peopleTable.count", { count: totalCount })}
            </p>
          )}
        </div>
        <ArtifactActions>
          {dismissAs === "close" && (
            <ArtifactAction
              aria-label={t("chat.peopleTable.close")}
              onClick={dismiss}
              tooltip={t("chat.peopleTable.close")}
            >
              <XIcon className="size-4" />
            </ArtifactAction>
          )}
        </ArtifactActions>
      </ArtifactHeader>
      {/* Laid over the conversation on a narrow screen: the end of the table
          must not scroll the thread behind it. */}
      <div className="overflow-auto overscroll-y-contain relative flex-1 p-4 min-h-0">
        {body === "error" ? (
          <ErrorMessage className="pt-10" />
        ) : body === "skeleton" ? (
          <PeopleTableSkeleton />
        ) : (
          <DataTableCompanyEmployees
            employees={employees}
            isLoading={isFilling}
            searchQuery={query}
            forceContactActions
            // Its rows are replaced on each one streamed: a reveal, a list
            // or a CRM sync clicked meanwhile would not last.
            areRowActionsLocked={areRowActionsLocked}
            // A reveal or a CRM sync lands in the cached search, which the
            // table is read from when it is reopened.
            onRowsChange={areRowChangesSaved ? handleRowsChange : undefined}
          />
        )}
      </div>
    </Artifact>
  );
}

/** The table filling up while its search runs, and how many it holds so far. */
function PeopleTableFilling({ totalCount }: { totalCount: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-1.5 items-center min-w-0 text-xs text-muted-foreground">
      <Loader2Icon className="size-3 shrink-0 animate-spin text-majorelle motion-reduce:animate-none" />
      <Shimmer className="truncate">
        {totalCount > 0
          ? t("chat.peopleTable.filling", { count: totalCount })
          : t("chat.peopleTable.searching")}
      </Shimmer>
    </div>
  );
}

/**
 * Stands in for the table while its saved rows load. Deliberately plain: the
 * table's own loading state mounts every column, and that cost would land in
 * the panel's opening animation.
 */
function PeopleTableSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 justify-between">
        <Skeleton className="w-[250px] h-8" />
        <Skeleton className="w-[320px] h-8" />
      </div>
      <div className="overflow-hidden rounded-xl border">
        <Skeleton className="h-12 rounded-none" />
        <div className="p-4 space-y-5">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-4" />
          ))}
        </div>
      </div>
    </div>
  );
}
